import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link as MuiLink,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormHelperText,
  Paper
} from '@mui/material';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import LockResetOutlined from '@mui/icons-material/LockResetOutlined';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import MarkEmailReadOutlined from '@mui/icons-material/MarkEmailReadOutlined';
import i18next from '../../lang';
import { rootStore } from '../../stores/RootStore';
import { observer } from 'mobx-react-lite';
import axios from "axios";



const Login = observer(() => {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [openPendingDialog, setOpenPendingDialog] = useState(false);
  const [openAccessDeniedDialog, setOpenAccessDeniedDialog] = useState(false);
  const [openMismatchDialog, setOpenMismatchDialog] = useState(false);
  const [isCapsLockOn, setIsCapsLockOn] = useState(false);
  const [isKorean, setIsKorean] = useState(false);
  const [isJapanese, setIsJapanese] = useState(false);
  const [openForgotIdDialog, setOpenForgotIdDialog] = useState(false);
  const [forgotIdEmail, setForgotIdEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [openFindIdSuccessDialog, setOpenFindIdSuccessDialog] = useState(false);
  const [openForgotPasswordDialog, setOpenForgotPasswordDialog] = useState(false);
  const [forgotPasswordId, setForgotPasswordId] = useState('');
  const [openResetPasswordSuccessDialog, setOpenResetPasswordSuccessDialog] = useState(false);
  const { authStore } = rootStore;

  // 이메일 유효성 검사
  const validateEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setForgotIdEmail(email);
    setIsEmailValid(validateEmail(email));
  };

  // CAPSLOCK 감지
  const handleCapsLock = (e: KeyboardEvent) => {
    try {
      setIsCapsLockOn(e.getModifierState('CapsLock'));
    } catch (error) {
      console.log('Error checking CapsLock state:', error);
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleCapsLock);
    document.addEventListener('keyup', handleCapsLock);

    return () => {
      document.removeEventListener('keydown', handleCapsLock);
      document.removeEventListener('keyup', handleCapsLock);
    };
  }, []);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);

    // 입력 언어 감지
    const isKoreanKeyboard = window.navigator.language === 'ko-KR';
    const isJapaneseKeyboard = window.navigator.language === 'ja-JP';

    // IME 입력 상태 감지
    if (e.nativeEvent instanceof CompositionEvent) {
      setIsKorean(isKoreanKeyboard);
      setIsJapanese(isJapaneseKeyboard);
    }
  };

  const getTemporaryOrder = async () => {
    try {
      const response = await axios.get('/api/v1/orders/temp');
      return response.data;

    } catch (error) {
      console.error('Failed to get temporary order：', error);
      return '';
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);

    if (!identifier.trim() || !password.trim()) return;

    try {
      const response = await authStore.login(identifier, password);
      if (response.result === 'SUCCESS') {
        const tempOrder = await getTemporaryOrder();
        navigate('/', { state: { tempOrder } });
      } else if (response.result === 'PENDING_APPROVAL') {
        setOpenPendingDialog(true);
      } else if (response.result === 'ACCESS_DENIED') {
        setOpenAccessDeniedDialog(true);
      } else {
        setOpenMismatchDialog(true);
      }
    } catch (err: any) {
      console.log(err.response?.data?.message || '로그인에 실패했습니다.')
    }
  };

  // 입력 상태 경고 메시지
  const getInputWarning = () => {
    const warnings = [];
    if (isCapsLockOn) warnings.push(i18next.t('auth.capslock_on'));
    if (isKorean) warnings.push(i18next.t('auth.korean_input'));
    if (isJapanese) warnings.push(i18next.t('auth.japanese_input'));
    return warnings.join('、');
  };

  const handleForgotIdSubmit = async () => {
    // ユーザー列挙脆弱性対策: APIの成否によらず成功ダイアログを表示する。
    // ただし通信障害（5xx/ネットワーク断）はユーザーに知らせる。
    // 4xx はバックエンドが常に200を返すため発生しないが、念のためスルーする。
    let networkError = false;
    try {
      await axios.post('/api/v1/auth/forgot-id', { identifier: forgotIdEmail, lang: i18next.language });
    } catch (error: any) {
      const status = error.response?.status;
      if (!status || status >= 500) {
        networkError = true;
      }
    } finally {
      setOpenForgotIdDialog(false);
      setForgotIdEmail('');
    }
    if (!networkError) {
      setOpenFindIdSuccessDialog(true);
    }
  };

  const handleForgotPasswordSubmit = async () => {
    let networkError = false;
    try {
      await axios.post('/api/v1/auth/forgot-password', { identifier: forgotPasswordId, lang: i18next.language });
    } catch (error: any) {
      const status = error.response?.status;
      if (!status || status >= 500) {
        networkError = true;
      }
    } finally {
      setOpenForgotPasswordDialog(false);
      setForgotPasswordId('');
    }
    if (!networkError) {
      setOpenResetPasswordSuccessDialog(true);
    }
  };

  return (
    <Box sx={{
      flex: 1,
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundImage: 'url(/assets/login_watercolor_bg.png)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }}>
      <Container maxWidth="sm">
        <Paper elevation={6} sx={{
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderRadius: 4,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)'
        }}>
          <Typography component="h1" variant="h4" sx={{ mb: 4 }}>
            {i18next.t('top.site_description')}
          </Typography>

          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%', mt: 1 }}>
            {authStore.error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {authStore.error}
              </Alert>
            )}
            <TextField
              margin="normal"
              required
              fullWidth
              id="identifier"
              label={i18next.t('common.id')}
              name="identifier"
              autoFocus
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              error={submitted && !identifier.trim()}
              helperText={submitted && !identifier.trim() ? i18next.t('error.enter_id') : ' '}
              InputProps={{ sx: { borderRadius: 2, bgcolor: 'rgba(255,255,255,0.8)' } }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={i18next.t('auth.password')}
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={handlePasswordChange}
              onCompositionStart={(e) => {
                const lang = e.currentTarget.lang;
                setIsKorean(lang === 'ko');
                setIsJapanese(lang === 'ja');
              }}
              onCompositionEnd={() => {
                setIsKorean(false);
                setIsJapanese(false);
              }}
              error={submitted && !password.trim()}
              helperText={submitted && !password.trim() ? i18next.t('error.enter_password') : ' '}
              InputProps={{ sx: { borderRadius: 2, bgcolor: 'rgba(255,255,255,0.8)' } }}
            />
            {(isCapsLockOn || isKorean || isJapanese) && (
              <FormHelperText error sx={{ ml: 1 }}>
                {getInputWarning()}
              </FormHelperText>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5, borderRadius: 3, fontSize: '1.1rem' }}
              disabled={authStore.isLoading}
            >
              {authStore.isLoading ? i18next.t('auth.logging_in') : i18next.t('auth.login')}
            </Button>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 3 }}>
              <MuiLink
                component="button"
                variant="body2"
                onClick={(e) => {
                  e.preventDefault();
                  setOpenForgotIdDialog(true);
                }}
                type="button"
              >
                {i18next.t('auth.forgot_id')}
              </MuiLink>
              <MuiLink
                component="button"
                variant="body2"
                onClick={(e) => {
                  e.preventDefault();
                  setOpenForgotPasswordDialog(true);
                }}
                type="button"
              >
                {i18next.t('auth.forgot_password')}
              </MuiLink>
            </Box>
          </Box>
        </Paper>

        <Dialog
          open={openPendingDialog}
          onClose={() => setOpenPendingDialog(false)}
        >
          <DialogTitle>{i18next.t('auth.pending_approval_title')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {i18next.t('auth.pending_approval_message')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenPendingDialog(false)}>
              {i18next.t('common.confirm')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openAccessDeniedDialog}
          onClose={() => setOpenAccessDeniedDialog(false)}
        >
          <DialogTitle>{i18next.t('auth.access_denied')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {i18next.t('auth.access_denied_message')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenAccessDeniedDialog(false)}>
              {i18next.t('common.confirm')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openMismatchDialog}
          onClose={() => setOpenMismatchDialog(false)}
        >
          <DialogTitle>{i18next.t('auth.info_mismatch')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {i18next.t('auth.check_id_password')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenMismatchDialog(false)}>
              {i18next.t('common.confirm')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ID検索 - 入力ダイアログ */}
        <Dialog
          open={openForgotIdDialog}
          onClose={() => setOpenForgotIdDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
        >
          <Box sx={{ bgcolor: '#F4D03F', py: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <EmailOutlined sx={{ fontSize: 48, color: '#1a1a1a', mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
              {i18next.t('auth.find_id')}
            </Typography>
          </Box>
          <DialogContent sx={{ pt: 3, pb: 1, px: 3 }}>
            <Box sx={{ bgcolor: '#fffde7', borderRadius: 2, p: 2, mb: 2.5, border: '1px solid #fff9c4' }}>
              <Typography variant="body2" sx={{ lineHeight: 1.8, color: '#5d4037' }}>
                {i18next.t('auth.forgot_id_guide')}
              </Typography>
            </Box>
            <TextField
              autoFocus
              margin="dense"
              label={i18next.t('contact.email_label')}
              type="email"
              fullWidth
              variant="outlined"
              value={forgotIdEmail}
              onChange={handleEmailChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && isEmailValid) {
                  e.preventDefault();
                  handleForgotIdSubmit();
                }
              }}
              error={forgotIdEmail !== '' && !isEmailValid}
              helperText={forgotIdEmail !== '' && !isEmailValid ? i18next.t('auth.invalid_email_format') : ''}
              InputProps={{ sx: { borderRadius: 2 } }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button
              onClick={() => (setOpenForgotIdDialog(false), setForgotIdEmail(''))}
              sx={{ borderRadius: 2 }}
            >
              {i18next.t('common.cancel')}
            </Button>
            <Button
              onClick={handleForgotIdSubmit}
              variant="contained"
              disabled={!isEmailValid}
              sx={{ borderRadius: 2, px: 3, bgcolor: '#F4D03F', color: '#1a1a1a', fontWeight: 600, '&:hover': { bgcolor: '#e6c235' } }}
            >
              {i18next.t('common.confirm')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ID検索成功ダイアログ */}
        <Dialog
          open={openFindIdSuccessDialog}
          onClose={() => setOpenFindIdSuccessDialog(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
        >
          <Box sx={{ bgcolor: '#F4D03F', py: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <MarkEmailReadOutlined sx={{ fontSize: 48, color: '#1a1a1a', mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
              {i18next.t('auth.find_id')}
            </Typography>
          </Box>
          <DialogContent sx={{ pt: 3, pb: 1, px: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, bgcolor: '#f0faf0', borderRadius: 2, p: 2, border: '1px solid #c8e6c9' }}>
              <CheckCircleOutline sx={{ color: '#4caf50', mt: 0.2, flexShrink: 0 }} />
              <Typography variant="body2" sx={{ lineHeight: 1.7, color: '#2e7d32' }}>
                {i18next.t('auth.id_sent_to_email')}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button
              onClick={() => setOpenFindIdSuccessDialog(false)}
              variant="contained"
              fullWidth
              sx={{ borderRadius: 2, py: 1.2, bgcolor: '#F4D03F', color: '#1a1a1a', fontWeight: 600, '&:hover': { bgcolor: '#e6c235' } }}
            >
              {i18next.t('common.confirm')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* パスワード検索 - 入力ダイアログ */}
        <Dialog
          open={openForgotPasswordDialog}
          onClose={() => setOpenForgotPasswordDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
        >
          <Box sx={{ bgcolor: '#F4D03F', py: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <LockResetOutlined sx={{ fontSize: 48, color: '#1a1a1a', mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
              {i18next.t('auth.find_password')}
            </Typography>
          </Box>
          <DialogContent sx={{ pt: 3, pb: 1, px: 3 }}>
            <Box sx={{ bgcolor: '#fffde7', borderRadius: 2, p: 2, mb: 2.5, border: '1px solid #fff9c4' }}>
              <Typography variant="body2" sx={{ lineHeight: 1.8, color: '#5d4037' }}>
                {i18next.t('auth.forgot_password_guide')}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#8d6e63' }}>
                {i18next.t('auth.password_reset_note')}
              </Typography>
            </Box>
            <TextField
              autoFocus
              margin="dense"
              label={i18next.t('common.id')}
              type="text"
              fullWidth
              variant="outlined"
              value={forgotPasswordId}
              onChange={(e) => setForgotPasswordId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && forgotPasswordId) {
                  e.preventDefault();
                  handleForgotPasswordSubmit();
                }
              }}
              InputProps={{ sx: { borderRadius: 2 } }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button
              onClick={() => (setOpenForgotPasswordDialog(false), setForgotPasswordId(''))}
              sx={{ borderRadius: 2 }}
            >
              {i18next.t('common.cancel')}
            </Button>
            <Button
              onClick={handleForgotPasswordSubmit}
              variant="contained"
              disabled={!forgotPasswordId}
              sx={{ borderRadius: 2, px: 3, bgcolor: '#F4D03F', color: '#1a1a1a', fontWeight: 600, '&:hover': { bgcolor: '#e6c235' } }}
            >
              {i18next.t('common.confirm')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* パスワードリセット成功ダイアログ */}
        <Dialog
          open={openResetPasswordSuccessDialog}
          onClose={() => setOpenResetPasswordSuccessDialog(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
        >
          <Box sx={{ bgcolor: '#F4D03F', py: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <MarkEmailReadOutlined sx={{ fontSize: 48, color: '#1a1a1a', mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
              {i18next.t('auth.find_password')}
            </Typography>
          </Box>
          <DialogContent sx={{ pt: 3, pb: 1, px: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, bgcolor: '#f0faf0', borderRadius: 2, p: 2, border: '1px solid #c8e6c9' }}>
              <CheckCircleOutline sx={{ color: '#4caf50', mt: 0.2, flexShrink: 0 }} />
              <Typography variant="body2" sx={{ lineHeight: 1.7, color: '#2e7d32' }}>
                {i18next.t('auth.password_reset_email_sent')}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button
              onClick={() => setOpenResetPasswordSuccessDialog(false)}
              variant="contained"
              fullWidth
              sx={{ borderRadius: 2, py: 1.2, bgcolor: '#F4D03F', color: '#1a1a1a', fontWeight: 600, '&:hover': { bgcolor: '#e6c235' } }}
            >
              {i18next.t('common.confirm')}
            </Button>
          </DialogActions>
        </Dialog>

      </Container>
    </Box>
  );
});

export default Login;